import { useEffect, useMemo, useState } from "react";
import { Button, Space, message } from "antd";
import { Link } from "react-router";
import { deliveryService } from "@/api/services/deliveryService";
import {
    ProductDetailTabSection,
    ProductDetailTabTable,
    emptyProductDetailColumns,
    type ProductDetailFilterValues,
    type ProductDetailTabProps
} from "@/views/pages/productManagement/ProductDetailTabPrimitives";
import { useUserStore } from "@/stores/react/authStore";
import { confirmDialog } from "@/utils/confirmDialog";
import { showRequestError } from "@/utils/requestErrorMessage";
import { DELIVERY_STATE, DELIVERY_TYPE, TRADE_ORDER_STATE } from "@zhihaoscm/meta";
import type { DeliveryVo } from "@zhihaoscm/entity";
import { TradeOrderDeliveryDialog } from "@/views/pages/tradeManage/tradeOrder/components/deliveryManagement/TradeOrderDeliveryDialog";

export type deliveryTableProps = ProductDetailTabProps;
const numberText = (value: unknown) =>
    value === null || value === undefined ? "--" : Number(value).toLocaleString("en-US");
const dateTimeText = (value: unknown) =>
    String(value || "--")
        .replace("T", " ")
        .slice(0, 16);

/** goodsManagement / components / delivery / deliveryTable：保留数据展示、编辑提交和父级事件回调。 */
export default function MigrationComponent(props: deliveryTableProps) {
    const productId = String(props.data?.productConsignment?.id || props.data?.id || "");
    const [rows, setRows] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [filters, setFilters] = useState<ProductDetailFilterValues>({});
    const [pageable, setPageable] = useState({ page: 1, size: 10 });
    const [reloadToken, setReloadToken] = useState(0);
    const [editingDelivery, setEditingDelivery] = useState<DeliveryVo | null>(null);
    const permissions = useUserStore(state => state.userInfo.permissions || []);
    const canDeal = permissions.includes("ROLE_TRADE_ORDER_DEAL");
    useEffect(() => {
        let disposed = false;
        if (!productId) return;
        // 旧 Vue deliveryTable 使用 productId 和当前分页条件查询提货明细。
        void deliveryService
            .getPaging({ page: pageable.page, size: pageable.size, productId, ...filters } as never)
            .then(result => {
                if (disposed) return;
                setTotal(Number(result?.total || result?.list?.length || 0));
                setRows(
                    (result?.list || []).map((item: any, index: number) => {
                        const delivery = item.delivery;
                        const order = item.order;
                        const deliveryState = Number(delivery?.state);
                        const orderLocked = [
                            Number(TRADE_ORDER_STATE.COMPLETED.code),
                            Number(TRADE_ORDER_STATE.CANCELLED.code)
                        ].includes(Number(order?.state));
                        const canEdit =
                            canDeal &&
                            !orderLocked &&
                            [
                                Number(DELIVERY_STATE.TO_BE_INITIATED.code),
                                Number(DELIVERY_STATE.CONFIRMED.code),
                                Number(DELIVERY_STATE.PICKED_UP.code)
                            ].includes(deliveryState);
                        const canCancel =
                            canDeal &&
                            !orderLocked &&
                            [
                                Number(DELIVERY_STATE.TO_BE_CONFIRMED.code),
                                Number(DELIVERY_STATE.TO_BE_INITIATED.code)
                            ].includes(deliveryState);
                        const canInitiate =
                            canDeal && !orderLocked && deliveryState === Number(DELIVERY_STATE.TO_BE_INITIATED.code);
                        const canDelete =
                            canDeal && !orderLocked && deliveryState === Number(DELIVERY_STATE.CANCELED.code);
                        return {
                            id: item.delivery?.id || `delivery-${index}`,
                            提货编号: delivery?.id ? (
                                <Link
                                    to={`/tradeManage/delivery/detail?id=${encodeURIComponent(String(delivery.id))}&orderId=${encodeURIComponent(String(order?.id || delivery.orderId || ""))}&orderOrigin=${encodeURIComponent(String(order?.orderOrigin || 1))}`}
                                >
                                    {delivery.deliveryId || delivery.id}
                                </Link>
                            ) : (
                                "--"
                            ),
                            提货日期: String(item.delivery?.scheduleDate || item.delivery?.deliveryDate || "--").slice(
                                0,
                                10
                            ),
                            结算金额: numberText(item.delivery?.settlementAmount ?? item.order?.settlementAmount),
                            "提货吨位（吨）": numberText(item.delivery?.estimatedTon),
                            "结算单价（元/吨）": numberText(item.delivery?.settlementUnitAmount),
                            状态:
                                DELIVERY_STATE.getIntroByCode(Number(item.delivery?.state)) ||
                                item.delivery?.stateName ||
                                "--",
                            关联排期: item.delivery?.scheduleId || "--",
                            关联订单: delivery?.orderId ? (
                                <Link to={`/tradeManage/tradeOrder/detail?id=${delivery.orderId}`}>
                                    {delivery.orderId}
                                </Link>
                            ) : (
                                "--"
                            ),
                            类型: DELIVERY_TYPE.getIntroByCode(Number(item.delivery?.type)) || "--",
                            创建时间: dateTimeText(item.delivery?.createdTime),
                            操作:
                                canEdit || canCancel || canInitiate || canDelete ? (
                                    <Space size={16}>
                                        {canEdit ? (
                                            <Button
                                                aria-label={`修改提货-${delivery?.id}`}
                                                size="small"
                                                type="link"
                                                onClick={() => {
                                                    setEditingDelivery(item);
                                                }}
                                            >
                                                修改
                                            </Button>
                                        ) : null}
                                        {canCancel ? (
                                            <Button
                                                aria-label={`取消提货-${delivery?.id}`}
                                                size="small"
                                                type="link"
                                                onClick={() => void mutateDelivery("cancel", item)}
                                            >
                                                取消提货
                                            </Button>
                                        ) : null}
                                        {canInitiate ? (
                                            <Button
                                                aria-label={`发起提货-${delivery?.id}`}
                                                size="small"
                                                type="link"
                                                onClick={() => void mutateDelivery("initiate", item)}
                                            >
                                                发起提货
                                            </Button>
                                        ) : null}
                                        {canDelete ? (
                                            <Button
                                                aria-label={`删除提货-${delivery?.id}`}
                                                size="small"
                                                type="link"
                                                onClick={() => void mutateDelivery("delete", item)}
                                            >
                                                删除
                                            </Button>
                                        ) : null}
                                    </Space>
                                ) : (
                                    ""
                                )
                        };
                    })
                );
            })
            .catch(() => {
                if (!disposed) setRows([]);
            });
        return () => {
            disposed = true;
        };
    }, [productId, filters, pageable, reloadToken, canDeal]);

    /** 提货行操作复用旧 Vue 的确认文案和服务接口，成功后刷新当前列表。 */
    const mutateDelivery = async (action: "cancel" | "initiate" | "delete", row: DeliveryVo) => {
        const id = row.delivery?.id;
        if (!id) return;
        const prompt =
            action === "initiate"
                ? "是否向买家发起提货"
                : action === "cancel"
                  ? "是否取消提货，取消后提货数据不可更改"
                  : "是否删除提货，删除后该数据将会消失";
        if (!(await confirmDialog(prompt))) return;
        try {
            if (action === "initiate") await deliveryService.initiateDelivery(String(id));
            if (action === "cancel") await deliveryService.cancelDelivery(String(id));
            if (action === "delete") await deliveryService.deleteDelivery(String(id));
            message.success(action === "initiate" ? "已发货" : "操作成功");
            setReloadToken(value => value + 1);
        } catch (error) {
            showRequestError(error, "提货操作失败");
        }
    };

    const columns = useMemo(() => {
        const result = emptyProductDetailColumns([
            "提货编号",
            "提货日期",
            "结算金额",
            "提货吨位（吨）",
            "结算单价（元/吨）",
            "状态",
            "关联排期",
            "关联订单",
            "类型",
            "创建时间",
            "操作"
        ]);
        // 旧版在横向滚动时始终保留提货编号，操作列由公共表格固定在右侧。
        result[0] = { ...result[0], fixed: "left" };
        return result;
    }, []);
    return (
        <ProductDetailTabSection title="提货明细" className="delivery-table" showTitle={false}>
            <ProductDetailTabTable
                ariaLabel="提货明细"
                columns={columns}
                data={rows}
                current={pageable.page}
                pageSize={pageable.size}
                total={total}
                onSearch={values => {
                    // 搜索沿用旧版行为：更新条件时回到第一页。
                    setPageable(current => ({ ...current, page: 1 }));
                    setFilters(values);
                }}
                onPageChange={(page, size) => setPageable({ page, size })}
            />
            {editingDelivery?.order ? (
                <TradeOrderDeliveryDialog
                    open={editingDelivery !== null}
                    editingDelivery={editingDelivery}
                    order={editingDelivery.order}
                    onCancel={() => setEditingDelivery(null)}
                    onSaved={() => setReloadToken(value => value + 1)}
                />
            ) : null}
            {props.children}
        </ProductDetailTabSection>
    );
}
